import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  isMultiMemberPackageKind,
  multiMemberPackageLabel,
} from "@/modules/private-lessons/domain/lib/package-kind";
import { formatDate } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Badge, Button, confirm, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  EditIcon,
  InfoIcon,
  PowerIcon,
  ShoppingCartIcon,
  TrashIcon,
} from "lucide-react";
import {
  useDeletePrivateLessonTemplate,
  usePrivateLessonTemplateQuery,
  usePrivateLessonTemplateUsageStatsQuery,
  useTogglePrivateLessonTemplateStatus,
} from "../api/queries";

export function PrivateLessonTemplateDetailPage() {
  const nav = useNavigate({
    from: "/app/private-lessons/templates/$id",
  });
  const { id } = useParams({ from: "/app/private-lessons/templates/$id" });
  const { data, ...result } = usePrivateLessonTemplateQuery(id);
  const { data: usageStats } = usePrivateLessonTemplateUsageStatsQuery(id);
  const toggleStatus = useTogglePrivateLessonTemplateStatus(id);
  const deleteTemplate = useDeletePrivateLessonTemplate();

  const template = data;

  const handleToggleStatus = async () => {
    if (!template) return;

    const newStatus = template.status === "active" ? "inactive" : "active";
    const statusLabel = newStatus === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";

    await toast.promise(toggleStatus.run(newStatus), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: `${statusLabel} template ແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ`,
      error: "ປ່ຽນສະຖານະ template ແພັກເກດຮຽນສ່ວນຕົວລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            {
              label: "Template ແພັກເກດຮຽນສ່ວນຕົວ",
              to: "/app/private-lessons/templates",
            },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-bold text-3xl tracking-tight">
            {template?.name || "Loading..."}
          </h1>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/private-lessons/templates" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນລາຍການ
          </Button>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ template ແພັກເກດຮຽນສ່ວນຕົວ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!template ? null : (
            <div className="space-y-6">
              {/* Usage Statistics Card */}

              <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                <div className="rounded-lg border p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ລວມທັງໝົດທີ່ຂາຍ
                  </p>
                  <p className="font-bold text-2xl">
                    {usageStats?.totalSold.toLocaleString("lo-LA") ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ແພັກເກດທີ່ໃຊ້ງານຢູ່
                  </p>
                  <p className="font-bold text-2xl text-green-700">
                    {usageStats?.active.toLocaleString("lo-LA") ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ໝົດອາຍຸ
                  </p>
                  <p className="font-bold text-2xl text-red-700">
                    {usageStats?.expired.toLocaleString("lo-LA") ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ໃຊ້ຄົບແລ້ວ
                  </p>
                  <p className="font-bold text-2xl text-orange-700">
                    {usageStats?.usedUp.toLocaleString("lo-LA") ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ຍົກເລີກ
                  </p>
                  <p className="font-bold text-2xl">
                    {usageStats?.canceled.toLocaleString("lo-LA") ?? 0}
                  </p>
                </div>
              </div>

              {/* General Information Card */}
              <div className="rounded-xl border bg-card">
                <div className="flex items-center justify-between border-b p-6">
                  <div className="flex items-center gap-2">
                    <InfoIcon className="size-5 text-primary" />
                    <h3 className="font-semibold text-lg">ຂໍ້ມູນທົ່ວໄປ</h3>
                  </div>
                  <div className="flex gap-2">
                    {template.status === "active" &&
                      template.packageKind !== "individual" && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() =>
                          nav({
                            to: "/app/sell",
                            search: {
                              type:
                                template.packageKind === "group"
                                  ? "private-lesson-group"
                                  : "private-lesson-shared",
                              templateId: id,
                            },
                          })
                        }
                      >
                        <ShoppingCartIcon className="size-4" />
                        ຂາຍແພັກເກດ
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        nav({
                          to: "/app/private-lessons/templates/$id/edit",
                          params: { id },
                        })
                      }
                    >
                      <EditIcon className="size-4" />
                      ແກ້ໄຂ Template
                    </Button>
                    <Button
                      variant={
                        template.status === "active" ? "destructive" : "default"
                      }
                      size="sm"
                      onClick={handleToggleStatus}
                      disabled={toggleStatus.isPending}
                    >
                      <PowerIcon className="size-4" />
                      {template.status === "active"
                        ? "ປິດການໃຊ້ງານ"
                        : "ເປີດການໃຊ້ງານ"}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={async () => {
                        const ok = await confirm({
                          title: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວ",
                          description:
                            "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ແພັກເກດຮຽນສ່ວນຕົວນີ້?",
                          actionText: "ລຶບ",
                          ActionProps: {
                            variant: "destructive",
                          },
                        });
                        if (!ok) return;

                        try {
                          await toast.promise(deleteTemplate.run(id), {
                            loading: "ກຳລັງລຶບ...",
                            success: () => {
                              nav({ to: "/app/private-lessons/templates" });
                              return "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ";
                            },
                            error: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວລົ້ມເຫຼວ",
                          });
                        } catch {
                          // Error handling is done by toast.promise
                        }
                      }}
                    >
                      <TrashIcon className="size-4" />
                      ລຶບ
                    </Button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຊື່ Template
                      </div>
                      <p className="font-semibold text-lg">{template.name}</p>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ສະຖານະ
                      </div>
                      <Badge
                        variant={
                          template.status === "active" ? "default" : "secondary"
                        }
                      >
                        {template.status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
                      </Badge>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ປະເພດແມ່ແບບ
                      </div>
                      <Badge
                        variant={
                          isMultiMemberPackageKind(template.packageKind)
                            ? "outline"
                            : "secondary"
                        }
                      >
                        {multiMemberPackageLabel(template.packageKind) ??
                          "ສ່ວນຕົວ"}
                      </Badge>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        {template.packageKind === "group"
                          ? "ຊົ່ວໂມງຕໍ່ກຸ່ມ"
                          : template.packageKind === "private_shared"
                            ? "ຊື້ຊົ່ວໂມງເອງ"
                            : "ຫນ່ວຍແມ່ແບບ"}
                      </div>
                      <p className="font-semibold text-lg">
                        {template.packageKind === "group"
                          ? `${template.totalHours} ຊົ່ວໂມງ`
                          : template.packageKind === "private_shared"
                            ? "ຕາມຈຳນວນທີ່ຂາຍ"
                            : "1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ"}
                      </p>
                    </div>
                    {isMultiMemberPackageKind(template.packageKind) &&
                      template.groupSizeMin != null &&
                      template.groupSizeMax != null && (
                        <div>
                          <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                            {template.packageKind === "private_shared"
                              ? "ຊ່ວງຈຳນວນຄົນ (tier)"
                              : "ຈຳນວນຄົນໃນກຸ່ມ"}
                          </div>
                          <p className="font-semibold text-lg">
                            {template.groupSizeMin}–{template.groupSizeMax} ຄົນ
                          </p>
                        </div>
                      )}
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຈຳນວນວັນ
                      </div>
                      <p className="font-semibold text-lg">
                        {template.validDays} ວັນ
                      </p>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ສະຖານທີ່
                      </div>
                      <Badge
                        variant={
                          template.defaultLocationKind === "off_site"
                            ? "outline"
                            : "secondary"
                        }
                      >
                        {template.defaultLocationKind === "off_site"
                          ? "ນອກສະຖານທີ່"
                          : "ສະຕູດິໂອ"}
                      </Badge>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        {template.packageKind === "group"
                          ? "ລາຄາຕໍ່ຄົນ"
                          : template.packageKind === "private_shared"
                            ? "ລາຄາ tier"
                            : "ລາຄາຕໍ່ຊົ່ວໂມງ"}
                      </div>
                      {template.packageKind === "private_shared" &&
                      template.tiers &&
                      template.tiers.length > 0 ? (
                        <div className="overflow-x-auto rounded-lg border">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-muted/40 text-left text-muted-foreground text-xs uppercase">
                                <th className="px-3 py-2">ຄົນ</th>
                                <th className="px-3 py-2 text-right">Studio ₭/ຊມ.</th>
                                <th className="px-3 py-2 text-right">ໃກ້ ₭/ຊມ.</th>
                                <th className="px-3 py-2 text-right">ໄກ ₭/ຊມ.</th>
                                <th className="px-3 py-2 text-right">ຄົນເພີ່ມ</th>
                              </tr>
                            </thead>
                            <tbody>
                              {template.tiers.map((tier, i) => (
                                <tr key={i} className="border-b last:border-0">
                                  <td className="px-3 py-2">
                                    {tier.minPeople}–{tier.maxPeople}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {tier.pricePerHour.toLocaleString("lo-LA")}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {tier.pricePerHourOffSiteNear != null
                                      ? tier.pricePerHourOffSiteNear.toLocaleString(
                                          "lo-LA",
                                        )
                                      : "—"}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {tier.pricePerHourOffSiteFar != null
                                      ? tier.pricePerHourOffSiteFar.toLocaleString(
                                          "lo-LA",
                                        )
                                      : "—"}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {tier.extraPersonPrice != null
                                      ? tier.extraPersonPrice.toLocaleString(
                                          "lo-LA",
                                        )
                                      : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="font-semibold text-lg text-primary">
                          {(template.packageKind === "group" &&
                          template.flatPrice != null
                            ? template.flatPrice
                            : template.pricePerHour
                          ).toLocaleString("lo-LA")}{" "}
                          ₭
                        </p>
                      )}
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີສ້າງ
                      </div>
                      <p className="font-semibold text-sm">
                        {formatDate(template.createdAt)}
                      </p>
                    </div>
                    <div>
                      <div className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີອັບເດດ
                      </div>
                      <p className="font-semibold text-sm">
                        {formatDate(template.updatedAt)}
                      </p>
                    </div>
                  </div>
                  {template.description && (
                    <div className="mt-6">
                      <div className="mb-2 block font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຄຳອະທິບາຍ
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {template.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
